import React, { useState } from 'react';
import { Search, Book, Award, Layers, ExternalLink, Loader, Info, CheckCircle, Clock, XCircle, AlertCircle, List } from 'lucide-react';
import toast from 'react-hot-toast';

export default function RncpSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedCertif, setSelectedCertif] = useState(null);
  const [enrichedData, setEnrichedData] = useState(null);
  const [loadingEnriched, setLoadingEnriched] = useState(false);

  // Recherche en masse
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkSearching, setBulkSearching] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkEnriching, setBulkEnriching] = useState(false);

  // Recherche dans la base de données locale
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Veuillez entrer un terme de recherche');
      return;
    }

    setSearching(true);
    setResults([]);
    setSelectedCertif(null);
    setEnrichedData(null);

    try {
      console.log(`🔍 Recherche RNCP: "${searchQuery}"`);

      // Recherche dans la BDD locale PostgreSQL
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/rncp/search?query=${encodeURIComponent(searchQuery)}&limit=20`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      console.log('📊 Résultats:', data);

      if (data.success) {
        setResults(data.data);
        toast.success(`${data.data.length} résultat(s) trouvé(s)`);
      } else {
        toast.error(data.message || 'Erreur lors de la recherche');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  };

  // Recherche en masse avec séparateur ;
  const handleBulkSearch = async () => {
    if (!bulkSearch.trim()) {
      toast.error('Veuillez entrer des codes RNCP ou titres séparés par ;');
      return;
    }

    // Séparer par ; et nettoyer
    const queries = bulkSearch.split(';').map(q => q.trim()).filter(q => q.length > 0);

    if (queries.length === 0) {
      toast.error('Aucune recherche valide détectée');
      return;
    }

    console.log(`🔍 Recherche en masse: ${queries.length} termes`);

    setBulkSearching(true);
    setBulkResults([]);

    const searchResults = [];

    // Rechercher chaque terme
    for (const query of queries) {
      const resultItem = {
        query,
        status: 'searching', // searching, found, not_found, error
        certification: null,
        enrichedData: null,
        enrichStatus: 'pending' // pending, enriching, enriched, error
      };

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/rncp/search?query=${encodeURIComponent(query)}&limit=1`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        const data = await response.json();

        if (data.success && data.data.length > 0) {
          resultItem.status = 'found';
          resultItem.certification = data.data[0];
        } else {
          resultItem.status = 'not_found';
        }
      } catch (error) {
        console.error(`❌ Erreur recherche ${query}:`, error);
        resultItem.status = 'error';
      }

      searchResults.push(resultItem);
      setBulkResults([...searchResults]); // Mise à jour progressive
    }

    setBulkSearching(false);
    toast.success(`Recherche terminée: ${searchResults.filter(r => r.status === 'found').length}/${queries.length} trouvé(s)`);
  };

  // Enrichir toutes les certifications trouvées
  const handleBulkEnrich = async () => {
    const toEnrich = bulkResults.filter(r => r.status === 'found' && r.enrichStatus === 'pending');

    if (toEnrich.length === 0) {
      toast.error('Aucune certification à enrichir');
      return;
    }

    setBulkEnriching(true);
    console.log(`🔍 Enrichissement en masse: ${toEnrich.length} certifications`);

    const updatedResults = [...bulkResults];

    for (const item of toEnrich) {
      const index = updatedResults.findIndex(r => r.query === item.query);

      if (index === -1) continue;

      updatedResults[index].enrichStatus = 'enriching';
      setBulkResults([...updatedResults]);

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/kit-apprentissage/enriched/${item.certification.code_rncp}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        const data = await response.json();

        if (data.success && data.data) {
          updatedResults[index].enrichedData = data.data;
          updatedResults[index].enrichStatus = 'enriched';
        } else {
          updatedResults[index].enrichStatus = 'error';
        }
      } catch (error) {
        console.error(`❌ Erreur enrichissement ${item.query}:`, error);
        updatedResults[index].enrichStatus = 'error';
      }

      setBulkResults([...updatedResults]);

      // Petite pause pour ne pas surcharger l'API
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    setBulkEnriching(false);

    const enrichedCount = updatedResults.filter(r => r.enrichStatus === 'enriched').length;
    toast.success(`Enrichissement terminé: ${enrichedCount}/${toEnrich.length} enrichi(s)`);
  };

  // Sélectionner une certification et charger les détails
  const handleSelectCertif = async (certif) => {
    setSelectedCertif(certif);
    setEnrichedData(null);

    // Charger les blocs et compétences depuis la BDD locale
    await loadCertifDetails(certif.code_rncp);
  };

  // Charger les détails depuis la BDD locale
  const loadCertifDetails = async (codeRncp) => {
    try {
      console.log(`📥 Chargement des détails pour RNCP ${codeRncp}`);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/rncp/${codeRncp}/details`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setSelectedCertif(prev => ({
          ...prev,
          blocks: data.blocks || [],
          competences: data.competences || []
        }));
      }
    } catch (error) {
      console.error('❌ Erreur chargement détails:', error);
    }
  };

  // Enrichir avec Kit Apprentissage
  const handleEnrich = async () => {
    if (!selectedCertif) return;

    setLoadingEnriched(true);

    try {
      console.log(`🔍 Enrichissement RNCP ${selectedCertif.code_rncp}`);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/kit-apprentissage/enriched/${selectedCertif.code_rncp}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      console.log('📊 Données enrichies:', data);

      if (data.success && data.data) {
        setEnrichedData(data.data);
        if (data._simulation) {
          toast.success('📊 Données simulées récupérées (Mode Démo)', { duration: 4000 });
        } else {
          toast.success('✅ Données enrichies récupérées !');
        }
      } else {
        toast.error(data.message || 'Aucune donnée supplémentaire disponible dans Kit Apprentissage');
      }
    } catch (error) {
      console.error('❌ Erreur enrichissement:', error);
      toast.error('Erreur lors de l\'enrichissement (vérifiez le token Kit Apprentissage)');
    } finally {
      setLoadingEnriched(false);
    }
  };

  // Icône de statut pour recherche en masse
  const getStatusIcon = (status) => {
    switch (status) {
      case 'searching':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'found':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'not_found':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  // Icône de statut d'enrichissement
  const getEnrichStatusIcon = (enrichStatus) => {
    switch (enrichStatus) {
      case 'enriching':
        return <Loader className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'enriched':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Recherche de Certifications RNCP
          </h1>
          <p className="text-gray-600">
            Recherchez parmi les {' '}
            <span className="font-semibold text-blue-600">9 374 certifications</span>
            {' '} RNCP importées dans la base de données locale
          </p>
        </div>

        {/* Recherche simple */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Search className="w-5 h-5" />
            Recherche Simple
          </h2>

          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Rechercher un titre de certification, un code RNCP..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              className="btn btn-primary flex items-center gap-2"
            >
              {searching ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              Rechercher
            </button>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <p className="flex items-center gap-2">
              <Info className="w-4 h-4" />
              Exemples : "Educateur sportif", "RNCP37439", "BPJEPS", "Commerce"
            </p>
          </div>
        </div>

        {/* Recherche en masse */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <List className="w-5 h-5 text-purple-600" />
            Recherche en Masse
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entrez plusieurs codes RNCP ou titres séparés par <code className="bg-purple-100 px-2 py-1 rounded text-purple-700">;</code>
              </label>
              <textarea
                value={bulkSearch}
                onChange={(e) => setBulkSearch(e.target.value)}
                placeholder="RNCP35044; RNCP37439; DSCG; Educateur sportif"
                rows="3"
                className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleBulkSearch}
                disabled={bulkSearching}
                className="btn btn-primary flex items-center gap-2"
              >
                {bulkSearching ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Recherche en cours...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Rechercher Tout
                  </>
                )}
              </button>

              {bulkResults.length > 0 && (
                <button
                  onClick={handleBulkEnrich}
                  disabled={bulkEnriching || bulkResults.filter(r => r.status === 'found' && r.enrichStatus === 'pending').length === 0}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  {bulkEnriching ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Enrichissement...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-5 h-5" />
                      Enrichir Tout ({bulkResults.filter(r => r.status === 'found' && r.enrichStatus === 'pending').length})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Résultats de recherche en masse */}
          {bulkResults.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Résultats ({bulkResults.length})
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {bulkResults.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {/* Icône de statut recherche */}
                      <div className="flex-shrink-0">
                        {getStatusIcon(item.status)}
                      </div>

                      {/* Infos */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">
                            {item.query}
                          </span>
                          {item.status === 'found' && item.certification && (
                            <span className="text-xs text-gray-500 font-mono">
                              RNCP{item.certification.code_rncp}
                            </span>
                          )}
                        </div>
                        {item.status === 'found' && item.certification && (
                          <p className="text-sm text-gray-600 truncate">
                            {item.certification.intitule}
                          </p>
                        )}
                        {item.status === 'not_found' && (
                          <p className="text-sm text-red-600">Non trouvé</p>
                        )}
                        {item.status === 'error' && (
                          <p className="text-sm text-orange-600">Erreur de recherche</p>
                        )}
                      </div>

                      {/* Statut enrichissement */}
                      {item.status === 'found' && (
                        <div className="flex items-center gap-2">
                          {getEnrichStatusIcon(item.enrichStatus)}
                          {item.enrichedData && (
                            <div className="flex items-center gap-2 text-xs">
                              {item.enrichedData.cfd && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                  CFD: {item.enrichedData.cfd}
                                </span>
                              )}
                              {item.enrichedData.nsf_rncp && item.enrichedData.nsf_rncp.length > 0 && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                  NSF: {item.enrichedData.nsf_rncp.join(', ')}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Résultats de recherche simple */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {results.length} résultat(s) trouvé(s)
            </h2>

            <div className="grid gap-3">
              {results.map((certif, index) => (
                <div
                  key={index}
                  onClick={() => handleSelectCertif(certif)}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedCertif?.code_rncp === certif.code_rncp
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-5 h-5 text-blue-600" />
                        <span className="font-mono text-sm font-semibold text-blue-600">
                          RNCP {certif.code_rncp}
                        </span>
                        {certif.niveau && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            Niveau {certif.niveau}
                          </span>
                        )}
                        {certif.romeCodesCount > 0 && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-semibold">
                            {certif.romeCodesCount} code{certif.romeCodesCount > 1 ? 's' : ''} ROME
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {certif.intitule}
                      </h3>
                      {certif.abrege && (
                        <p className="text-sm text-gray-600">
                          {certif.abrege}
                        </p>
                      )}
                      {certif.romeCodes && certif.romeCodes.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-medium text-gray-600 mb-1">Codes ROME:</div>
                          <div className="flex flex-wrap gap-1">
                            {certif.romeCodes.slice(0, 3).map((rome, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                                <span className="font-mono font-semibold">{rome.code}</span>
                                <span className="text-gray-600">-</span>
                                <span>{rome.libelle}</span>
                              </span>
                            ))}
                            {certif.romeCodes.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                +{certif.romeCodes.length - 3} autre{certif.romeCodes.length - 3 > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {certif.url_fiche && (
                        <a
                          href={certif.url_fiche}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
                        >
                          Voir la fiche sur France Compétences
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Détails de la certification sélectionnée */}
        {selectedCertif && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Détails de la Certification
                </h2>
                <p className="text-gray-600">
                  RNCP {selectedCertif.code_rncp} - {selectedCertif.intitule}
                </p>
              </div>
              <button
                onClick={handleEnrich}
                disabled={loadingEnriched}
                className="btn btn-secondary flex items-center gap-2"
              >
                {loadingEnriched ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4" />
                    Enrichir avec Kit Apprentissage
                  </>
                )}
              </button>
            </div>

            {/* Blocs de compétences */}
            {selectedCertif.blocks && selectedCertif.blocks.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-green-600" />
                  Blocs de Compétences ({selectedCertif.blocks.length})
                </h3>
                <div className="space-y-3">
                  {selectedCertif.blocks.map((block, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-green-700">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">
                            {block.code || `Bloc ${index + 1}`}
                          </h4>
                          <p className="text-gray-700 mt-1">
                            {block.intitule || block.libelle}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Compétences */}
            {selectedCertif.competences && selectedCertif.competences.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Book className="w-5 h-5 text-purple-600" />
                  Compétences ({selectedCertif.competences.length})
                </h3>
                <div className="grid gap-2">
                  {selectedCertif.competences.map((comp, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                      <p className="text-gray-700">
                        {comp.intitule || comp.libelle || comp.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Données enrichies Kit Apprentissage */}
        {enrichedData && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-blue-600" />
              Données Kit Apprentissage
              {enrichedData.metadata?.simulation && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                  🎭 Mode Simulation
                </span>
              )}
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {enrichedData.cfd && (
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Code Formation Diplôme (CFD)</p>
                  <p className="font-mono font-semibold text-lg text-gray-900">
                    {enrichedData.cfd}
                  </p>
                </div>
              )}

              {enrichedData.nsf_rncp && enrichedData.nsf_rncp.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Codes NSF</p>
                  <div className="flex flex-wrap gap-2">
                    {enrichedData.nsf_rncp.map((nsf, i) => (
                      <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-mono text-sm">
                        {nsf}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {enrichedData.niv_fiche_RNCP && (
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Niveau</p>
                  <p className="font-semibold text-lg text-gray-900">
                    Niveau {enrichedData.niv_fiche_RNCP}
                  </p>
                </div>
              )}

              {enrichedData.etat_fiche && (
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">État de la fiche</p>
                  <p className={`font-semibold text-lg ${
                    enrichedData.etat_fiche === 'ACTIF' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {enrichedData.etat_fiche}
                  </p>
                </div>
              )}
            </div>

            {/* JSON complet */}
            <details className="mt-4">
              <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">
                Voir les données complètes (JSON)
              </summary>
              <pre className="mt-3 bg-white p-4 rounded-lg overflow-auto text-xs border border-gray-200">
                {JSON.stringify(enrichedData, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center gap-2">
            <Info className="w-5 h-5" />
            Informations
          </h3>
          <div className="text-sm text-yellow-800 space-y-2">
            <p>
              <strong>Source des données RNCP :</strong> Base de données locale PostgreSQL (synchronisée depuis data.gouv.fr)
            </p>
            <p>
              <strong>Kit Apprentissage :</strong> Pour enrichir les données, vous devez obtenir un token d'accès (voir KIT_APPRENTISSAGE_SETUP.md)
            </p>
            <p>
              <strong>Recherche en masse :</strong> Séparez les codes RNCP ou titres par <code className="bg-yellow-100 px-1 rounded">;</code> pour rechercher plusieurs certifications à la fois
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
